import { useMemo } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts/highstock';
import accessibilityInit from 'highcharts/modules/accessibility';

import { getLocalTimezoneAbbreviation } from '@/components/pages/assets/capacity-test/utils';
import { formatAmpere, formatVolt } from '@/utils/format/battery';
import { formatPercentage } from '@/utils/format/number';
import { formatTemperature } from '@/utils/format/temperature';

import { GetChartData } from '../api/queries/get-chart-data';
import { ChartType } from '../entities/chart-type';
import type { Filters } from '../entities/filters';
import type { Node } from '../entities/node';

import { EmptyChartState } from './empty-chart-state';

accessibilityInit(Highcharts);

export type ChartProps = {
  requests: { node: Node; query: UseQueryResult<GetChartData.Output> }[];
  chartType: ChartType;
  from: Date;
  to: Date;
  filterNoise: boolean;
  onUpdateFilters: (updated: Partial<Filters>) => void;
};

export function Chart({
  requests,
  chartType,
  from,
  to,
  filterNoise,
  onUpdateFilters,
}: ChartProps) {
  const options = useMemo(
    () => optionsFromRequests(requests, chartType, from, to),
    [requests, chartType, from, to],
  );

  const hasSeries =
    Array.isArray(options?.series) &&
    (options.series as Highcharts.SeriesOptionsType[]).length > 0;

  if (!options || !hasSeries) {
    return (
      <div className="chartContainer">
        <EmptyChartState
          chartType={chartType}
          from={from}
          to={to}
          filterNoise={filterNoise}
          onUpdateFilters={onUpdateFilters}
          compact
        />
      </div>
    );
  }

  return (
    <div className="chartContainer">
      <HighchartsReact
        highcharts={Highcharts}
        constructorType="stockChart"
        options={options}
      />
    </div>
  );
}

function optionsFromRequests(
  requests: { node: Node; query: UseQueryResult<GetChartData.Output> }[],
  chartType: ChartType,
  from: Date,
  to: Date,
): Highcharts.Options | undefined {
  const label = chartTypeLabel[chartType];
  const valueFormatter = chartTypeFormatter[chartType];

  const timezone = getLocalTimezoneAbbreviation(from);

  const series = requests.flatMap<Highcharts.SeriesOptionsType>(function ({
    query,
    node,
  }) {
    if (query.isLoading) {
      return [{ type: 'line', name: nameFromNode(node) + ' ...' }];
    }

    if (query.isError) {
      return [];
    }

    return query.data[chartType].map(({ column, data }) => ({
      type: 'line',
      name: nameFromNode(node, column),
      data,
    }));
  });

  return {
    series,
    colors: ['#86dec2', '#a6c9be', '#bfbfbf', '#c78402', '#9f8671'],
    plotOptions: {
      series: {
        borderColor: '#000000',
        pointStart: 2010,
        marker: { enabled: false },
        states: {
          hover: {
            enabled: false,
          },
        },
      },
    },
    legend: {
      enabled: true,
      align: 'right',
      verticalAlign: 'top',
      layout: 'vertical',
    },
    title: { text: undefined },
    time: { useUTC: false },
    xAxis: {
      type: 'datetime',
      min: from.getTime(),
      max: to.getTime(),
      crosshair: true,
      gridLineWidth: 0,
      ordinal: false,
      events: {
        afterSetExtremes: syncChartsExtremes,
      },
    },
    yAxis: {
      opposite: false,
      title: {
        text: label,
        style: { fontSize: '1rem' },
      },
      labels: {
        formatter() {
          return valueFormatter(Number(this.value));
        },
      },
    },
    tooltip: {
      pointFormatter() {
        return `${this.series.name}: <strong>${valueFormatter(Number(this.y))}</strong>`;
      },
      xDateFormat: `%Y-%m-%d %H:%M:%S (${timezone})`,
    },
    chart: {
      animation: false,
      type: 'line',
      borderWidth: 1,
      borderColor: 'none',
      height: 'auto',
      marginLeft: 100,
      marginRight: 230,
      zooming: {
        type: 'x',
        mouseWheel: false,
      },
    },
    navigator: { enabled: true },
    rangeSelector: { enabled: false },
    credits: { enabled: false },
  };
}

function syncChartsExtremes(
  this: Highcharts.Axis,
  a: Highcharts.AxisSetExtremesEventObject,
) {
  for (const chart of Highcharts.charts) {
    if (!chart || chart === this.chart) {
      continue;
    }

    const axis = chart.xAxis.at(0);

    if (!axis) {
      continue;
    }

    const b = axis.getExtremes();

    if (a.min === b.min && a.max === b.max) {
      continue;
    }

    axis.setExtremes(a.min, a.max, true, false);
  }
}

const chartTypeLabel: Record<ChartType, string> = {
  [ChartType.VOLTAGE]: 'Voltage',
  [ChartType.CURRENT]: 'Current',
  [ChartType.SOC]: 'State of Charge',
  [ChartType.TEMPERATURE]: 'Temperature',
  [ChartType.CELL_VOLTAGE]: 'Cell Voltage',
};

const chartTypeFormatter: Record<ChartType, (value: number) => string> = {
  [ChartType.VOLTAGE]: (value) => formatVolt(value, 1, 2),
  [ChartType.CURRENT]: (value) => formatAmpere(value, 1, 2),
  [ChartType.SOC]: (value) => formatPercentage(value / 100, 1, 2),
  [ChartType.TEMPERATURE]: (value) => formatTemperature(value, 1, 2),
  [ChartType.CELL_VOLTAGE]: (value) => formatVolt(value, 1, 2),
};

function nameFromNode(node: Node, column?: string): string {
  let suffix = '';

  if (column?.endsWith('_avg')) {
    suffix = ' (avg)';
  } else if (column?.endsWith('_min')) {
    suffix = ' (min)';
  } else if (column?.endsWith('_max')) {
    suffix = ' (max)';
  }

  return node.label + suffix;
}
